import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMyMenus } from "@/hooks/useMyMenus";
import { PageHeader, StatCard } from "@/components/app/UI";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { Package, TrendingDown, TrendingUp, AlertTriangle, Plus, Trash2 } from "lucide-react"; // eslint-disable-line no-unused-vars
import { toast } from "sonner";

// Simple locale-aware number formatter for the Stock module.
const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));

const StockTabs = () => {
  const loc = useLocation();
  const tabs = [
    { to: "/stock", label: "Dashboard", end: true },
    { to: "/stock/current", label: "Current Stock" },
    { to: "/stock/ledger", label: "Stock Ledger" },
    { to: "/stock/low", label: "Low Stock" },
    { to: "/stock/reports", label: "Reports" },
  ];
  return (
    <div className="border-b border-[#E5E7EB] flex flex-wrap gap-1 px-4 sm:px-8 pt-4" data-testid="stock-tabs">
      {tabs.map((t) => {
        const active = t.end ? loc.pathname === t.to : loc.pathname.startsWith(t.to);
        return (
          <Link
            key={t.to}
            to={t.to}
            className={`px-4 h-9 flex items-center text-[11px] uppercase tracking-widest font-bold border ${active ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
            data-testid={`stock-tab-${t.label.toLowerCase().replace(/\s/g, "-")}`}
          >{t.label}</Link>
        );
      })}
    </div>
  );
};

// ================================================= REPORTS ==================
export function StockReports() {
  const REPORT_TYPES = [
    { v: "current_stock", label: "Current Stock" },
    { v: "stock_movement", label: "Stock Movement" },
    { v: "low_stock", label: "Low Stock" },
    { v: "grn_vs_delivery", label: "GRN vs Delivery" },
    { v: "project_delivery", label: "Project-wise Delivery" },
    { v: "pending_material", label: "Pending Material" },
  ];
  const [f, setF] = useState({ report_type: "current_stock", date_from: "", date_to: "", item: "", brand: "", project_id: "" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects/options").then((r) => setProjects(r.data || [])); }, []);
  const generate = async () => {
    setLoading(true);
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.post("/stock/reports", payload);
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };
  const download = async (kind) => {
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.post(`/stock/reports/${kind}`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${f.report_type}.${kind === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to export"); }
  };
  return (
    <div data-testid="stock-reports-page">
      <PageHeader eyebrow="Warehouse" title="Stock Reports" subtitle="9 reports covering movement, deliveries and shortages. Export to Excel or PDF." />
      <StockTabs />
      <div className="p-4 sm:p-8 space-y-4">
        <div className="flat-card p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="label-eyebrow">Report</Label>
              <select
                value={f.report_type}
                onChange={(e) => { setF({ ...f, report_type: e.target.value }); setPreview(null); }}
                className="rounded-none mt-2 h-10 w-full border border-[#E5E7EB] px-3 text-sm bg-white"
                data-testid="report-type"
              >
                {REPORT_TYPES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Date From</Label>
              <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="rounded-none mt-2 h-10" data-testid="report-date-from" />
            </div>
            <div>
              <Label className="label-eyebrow">Date To</Label>
              <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="rounded-none mt-2 h-10" data-testid="report-date-to" />
            </div>
            <div>
              <Label className="label-eyebrow">Item Contains</Label>
              <Input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} className="rounded-none mt-2 h-10" data-testid="report-item" />
            </div>
            <div>
              <Label className="label-eyebrow">Brand</Label>
              <Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className="rounded-none mt-2 h-10" data-testid="report-brand" />
            </div>
            <div>
              <Label className="label-eyebrow">Project</Label>
              <select
                value={f.project_id}
                onChange={(e) => setF({ ...f, project_id: e.target.value })}
                className="rounded-none mt-2 h-10 w-full border border-[#E5E7EB] px-3 text-sm bg-white"
                data-testid="report-project"
              >
                <option value="">All projects</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name} ({p.qr_code})</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={generate} disabled={loading} className="rounded-none bg-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="report-generate">
              {loading ? "Loading…" : "Generate"}
            </Button>
            <Button onClick={() => download("xlsx")} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="report-export-xlsx">Export Excel</Button>
            <Button onClick={() => download("pdf")} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="report-export-pdf">Export PDF</Button>
          </div>
        </div>
        {preview && (
          <div className="flat-card overflow-x-auto" data-testid="report-preview">
            <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <div>
                <div className="font-black text-xl">{preview.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{preview.rows.length} rows</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>{preview.columns.map((c) => <th key={c} className="text-left label-eyebrow !text-[10px] px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 && <tr><td colSpan={preview.columns.length} className="text-center text-sm text-[#9CA3AF] py-12">No matching records.</td></tr>}
                {preview.rows.slice(0, 500).map((row, i) => (
                  <tr key={i} className="border-b border-[#F3F4F6]">
                    {row.map((c, j) => <td key={j} className="px-3 py-2 text-xs whitespace-nowrap">{c === null || c === undefined ? "—" : String(c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 500 && (
              <div className="p-3 text-[10px] text-[#4B5563] text-center border-t border-[#E5E7EB]">
                Showing first 500 rows in preview. Export to Excel/PDF for the complete report.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ================================================= DASHBOARD ================
export function StockDashboard() {
  const [d, setD] = useState(null);
  const load = () => api.get("/stock/dashboard").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div data-testid="stock-dashboard-page">
      <PageHeader eyebrow="Warehouse" title="Stock Management" subtitle="Live inventory ledger. Every GRN adds stock; every delivered qty removes it." />
      <StockTabs />
      <div className="p-4 sm:p-8 space-y-6">
        {isAdmin && <StockCleanupCard onDone={load} />}
        {d && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Items" value={fmt(d.total_items)} testid="stat-total-items" />
              <StatCard label="Low Stock" value={fmt(d.low_stock_count)} accent={d.low_stock_count > 0 ? "#DC2626" : undefined} testid="stat-low-stock" />
              <StatCard label="Out of Stock" value={fmt(d.out_of_stock_count)} accent={d.out_of_stock_count > 0 ? "#DC2626" : undefined} testid="stat-out-of-stock" />
              <StatCard label="Today's Dispatches" value={fmt(d.todays_dispatch_entries)} testid="stat-dispatches" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="flat-card p-5">
                <div className="label-eyebrow flex items-center gap-2"><AlertTriangle className="w-3 h-3 text-[#DC2626]" /> Low Stock Items</div>
                <div className="mt-3 space-y-1.5">
                  {d.low_stock_items.length === 0 ? (
                    <div className="text-xs text-[#9CA3AF]">No items below minimum stock level.</div>
                  ) : d.low_stock_items.map((r) => (
                    <div key={r.stock_key} className="flex justify-between items-center text-sm border-b border-[#F3F4F6] py-1.5 last:border-0">
                      <div>
                        <span className="font-semibold">{r.item_name}</span>
                        {r.brand && <span className="text-[10px] text-[#4B5563] ml-2">{r.brand}</span>}
                      </div>
                      <div className="text-xs">
                        <span className="text-[#DC2626] font-bold">{fmt(r.on_hand)}</span>
                        <span className="text-[#9CA3AF]"> / min {fmt(r.minimum_stock_level)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flat-card p-5">
                <div className="label-eyebrow">Recent Movements</div>
                <div className="mt-3 space-y-1.5">
                  {(d.recent_movements || []).slice(0, 10).map((m) => (
                    <div key={m.id} className="flex justify-between items-center text-xs border-b border-[#F3F4F6] py-1.5 last:border-0">
                      <div className="min-w-0 flex-1 truncate">
                        <span className={m.qty_delta > 0 ? "text-[#059669]" : "text-[#DC2626]"}>
                          {m.qty_delta > 0 ? "+" : ""}{fmt(m.qty_delta)}
                        </span>
                        <span className="ml-2 font-semibold">{m.item_name}</span>
                        {m.brand && <span className="text-[10px] text-[#4B5563] ml-1">({m.brand})</span>}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] whitespace-nowrap ml-2">{m.source}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================== CURRENT STOCK ==================
export function StockCurrent() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [deleting, setDeleting] = useState(null);
  const load = () => api.get("/stock/current", { params: { search: q || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteRow = async (r) => {
    const label = `${r.item_name}${r.brand ? ` (${r.brand})` : ""} — ${r.unit || "unit"}`;
    if (!window.confirm(`Delete "${label}" from Current Stock?\n\nAll ledger entries for this item will be permanently removed. This cannot be undone.`)) return;
    const reason = window.prompt("Reason for deletion (optional):", "Duplicate item") || "";
    setDeleting(r.stock_key);
    try {
      const { data } = await api.post("/stock/delete-row", { item_name: r.item_name, brand: r.brand || "", unit: r.unit || "", reason });
      toast.success(`Deleted "${r.item_name}" — ${data.deleted} ledger entr${data.deleted === 1 ? "y" : "ies"} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="stock-current-page">
      <PageHeader eyebrow="Warehouse" title="Current Stock" subtitle="Live on-hand balance per (item × brand × unit) computed from the ledger." />
      <StockTabs />
      <div className="p-4 sm:p-8">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item or brand…" className="rounded-none h-10 max-w-md mb-4" data-testid="stock-current-search" />
        <div className="flat-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Item", "Brand", "Unit", "Received", "Delivered", "On Hand", "Min Level", "Status", ...(isAdmin ? [""] : [])].map((h) => <th key={h || "actions"} className="text-left label-eyebrow !text-[10px] px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="text-center text-sm text-[#9CA3AF] py-12">No stock records yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.stock_key} className={`border-b border-[#F3F4F6] ${r.is_low ? "bg-[#FEF2F2]" : ""}`} data-testid={`stock-row-${r.stock_key}`}>
                  <td className="px-4 py-3 font-semibold">{r.item_name}</td>
                  <td className="px-4 py-3 text-xs">{r.brand || "—"}</td>
                  <td className="px-4 py-3 text-xs">{r.unit || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#059669] font-bold">{fmt(r.total_in)}</td>
                  <td className="px-4 py-3 text-xs text-[#DC2626] font-bold">{fmt(r.total_out)}</td>
                  <td className="px-4 py-3 text-sm font-black">{fmt(r.on_hand)}</td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{fmt(r.minimum_stock_level)}</td>
                  <td className="px-4 py-3">
                    {r.on_hand <= 0 ? (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEE2E2] text-[#991B1B]">Out</span>
                    ) : r.is_low ? (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEF3C7] text-[#92400E]">Low</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 bg-[#D1FAE5] text-[#065F46]">OK</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => deleteRow(r)}
                        disabled={deleting === r.stock_key}
                        className="p-1.5 text-[#9CA3AF] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors disabled:opacity-40"
                        title="Delete item from Current Stock"
                        data-testid={`stock-delete-btn-${r.stock_key}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ================================================ LEDGER ====================
export function StockLedger() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("");
  const load = () => api.get("/stock/ledger", { params: { source: source || undefined, limit: 500 } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [source]); // eslint-disable-line react-hooks/exhaustive-deps
  const { user } = useAuth();
  const { menus } = useMyMenus(user?.role);
  const canAdjust = user?.role === "admin" || !!menus?.has("stock-adjust");
  return (
    <div data-testid="stock-ledger-page">
      <PageHeader eyebrow="Warehouse" title="Stock Ledger" subtitle="Chronological log of every stock-in and stock-out event." action={canAdjust && <ManualAdjustDialog onDone={load} />} />
      <StockTabs />
      <div className="p-4 sm:p-8">
        <div className="mb-4 flex gap-2">
          {["", "grn", "delivery", "adjustment"].map((s) => (
            <button key={s} onClick={() => setSource(s)}
              className={`px-3 h-8 text-[10px] uppercase tracking-widest font-bold border ${source === s ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
              data-testid={`ledger-filter-${s || "all"}`}
            >{s || "All"}</button>
          ))}
        </div>
        <div className="flat-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Date", "Item", "Brand", "Type", "Qty", "Balance", "Source", "Reference", "Project", "By"].map((h) => <th key={h} className="text-left label-eyebrow !text-[10px] px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={10} className="text-center text-sm text-[#9CA3AF] py-12">No ledger entries yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F3F4F6]">
                  <td className="px-3 py-2 text-xs">{(r.at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 font-semibold">{r.item_name}</td>
                  <td className="px-3 py-2 text-xs">{r.brand || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${r.movement_type === "in" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                      {r.movement_type}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-xs font-bold ${r.qty_delta > 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>
                    {r.qty_delta > 0 ? "+" : ""}{fmt(r.qty_delta)} {r.unit}
                  </td>
                  <td className="px-3 py-2 text-xs font-bold">{fmt(r.balance_after)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#4B5563]">{r.source}</td>
                  <td className="px-3 py-2 text-xs font-mono">{r.source_no || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.project_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-[#4B5563]">{r.user_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================ LOW STOCK =====================
export function StockLow() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/stock/current", { params: { only_low: true } }).then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="stock-low-page">
      <PageHeader eyebrow="Warehouse" title="Low Stock" subtitle="Items whose on-hand balance is at or below the configured minimum level." />
      <StockTabs />
      <div className="p-4 sm:p-8">
        <div className="flat-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Item", "Brand", "Unit", "On Hand", "Minimum", "Shortage"].map((h) => <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center text-sm text-[#9CA3AF] py-12">Everything is above minimum stock level.</td></tr>}
              {rows.map((r) => {
                const shortage = Math.max((r.minimum_stock_level || 0) - r.on_hand, 0);
                return (
                  <tr key={r.stock_key} className="border-b border-[#F3F4F6] bg-[#FEF2F2]">
                    <td className="px-4 py-3 font-semibold">{r.item_name}</td>
                    <td className="px-4 py-3 text-xs">{r.brand || "—"}</td>
                    <td className="px-4 py-3 text-xs">{r.unit || "—"}</td>
                    <td className="px-4 py-3 text-sm font-black text-[#DC2626]">{fmt(r.on_hand)}</td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{fmt(r.minimum_stock_level)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#DC2626]">{fmt(shortage)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================ MANUAL ADJUST =================
// One-time production data cleanup — REMOVE this card once cleanup is confirmed.
function StockCleanupCard({ onDone }) {
  const [busy, setBusy] = useState("");
  const removeInvalid = async () => {
    if (!window.confirm("Delete ALL stock ledger rows with a blank or 'Unknown' item name?\n\nThey disappear from the Ledger and from Current Stock. This cannot be undone.")) return;
    setBusy("invalid");
    try {
      const { data } = await api.post("/stock/cleanup/invalid");
      toast.success(`Removed ${data.deleted_total} invalid entries (${data.deleted_blank} blank, ${data.deleted_unknown} Unknown)`);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
    setBusy("");
  };
  const resetZero = async () => {
    if (!window.confirm("Zero out ALL stock quantities?\n\nA counter-adjustment is added per item so every on-hand balance becomes 0. Ledger history is preserved (entries tagged RESET). You can then re-add stock via Manual Adjustment.")) return;
    setBusy("reset");
    try {
      const { data } = await api.post("/stock/reset-zero");
      toast.success(`Stock reset — ${data.zeroed_count} item(s) brought to zero`);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setBusy("");
  };
  return (
    <div className="flat-card p-5 border-[#DC2626]" data-testid="stock-cleanup-card">
      <div className="label-eyebrow flex items-center gap-2 text-[#DC2626]">
        <AlertTriangle className="w-3 h-3" /> Data Cleanup (Admin · one-time)
      </div>
      <div className="text-xs text-[#4B5563] mt-2">
        Run <b>1</b> first to remove blank/Unknown ledger rows, then <b>2</b> to zero all quantities before re-adding stock via Manual Adjustment.
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={removeInvalid} disabled={!!busy} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] uppercase tracking-widest text-xs font-bold h-10" data-testid="cleanup-invalid-btn">
          {busy === "invalid" ? "Working…" : "1 · Remove Invalid Entries"}
        </Button>
        <Button onClick={resetZero} disabled={!!busy} className="rounded-none bg-[#DC2626] hover:bg-[#B91C1C] uppercase tracking-widest text-xs font-bold h-10" data-testid="reset-zero-btn">
          {busy === "reset" ? "Working…" : "2 · Zero Out All Stock"}
        </Button>
      </div>
    </div>
  );
}

// ============================================ MANUAL ADJUST =================
function ManualAdjustDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", item_name: "", brand: "", unit: "", qty_delta: "", reason: "" });
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  // Load the product + brand master when the dialog opens so Item Name +
  // Brand are picked from the same catalog as Purchase Order line items.
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/purchasing/products").then((r) => r.data || []).catch(() => []),
      api.get("/purchasing/brands").then((r) => r.data || []).catch(() => []),
    ]).then(([ps, bs]) => { setProducts(ps); setBrands(bs); });
  }, [open]);
  const brandsForProduct = useMemo(
    () => brands.filter((b) => b.product_id === f.product_id && b.active !== false),
    [brands, f.product_id],
  );
  const pickProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    setF((prev) => ({
      ...prev,
      product_id: pid,
      item_name: p?.name || "",
      unit: p?.unit_of_measure || prev.unit,
      brand: "",  // reset — old brand may not belong to the new product
    }));
  };
  const submit = async () => {
    if (!f.product_id) { toast.error("Pick a product for Item Name"); return; }
    try {
      await api.post("/stock/adjust", { item_name: f.item_name, brand: f.brand, unit: f.unit, qty_delta: Number(f.qty_delta), reason: f.reason });
      toast.success("Adjustment recorded");
      setOpen(false); setF({ product_id: "", item_name: "", brand: "", unit: "", qty_delta: "", reason: "" });
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-[#F59E0B] hover:bg-[#D97706] uppercase tracking-widest text-xs font-bold h-11" data-testid="stock-adjust-open">
          <Plus className="w-4 h-4 mr-2" /> Manual Adjustment
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-md" data-testid="stock-adjust-dialog">
        <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Manual Stock Adjustment</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <Label className="label-eyebrow">Item Name</Label>
            <SearchablePicker
              items={products}
              value={f.product_id}
              onChange={pickProduct}
              placeholder="Search product…"
              testid="adjust-item"
              selectedLabel={(p) => p.name}
              matchItem={productMatch}
              renderRow={renderProductRow}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Brand (optional)</Label>
              <Select
                value={f.brand || "_n"}
                onValueChange={(v) => setF({ ...f, brand: v === "_n" ? "" : v })}
                disabled={!f.product_id}
              >
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="adjust-brand">
                  <SelectValue placeholder={f.product_id ? (brandsForProduct.length ? "— pick brand —" : "No brands for this product") : "Pick a product first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_n">— none —</SelectItem>
                  {brandsForProduct.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Unit</Label>
              <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="Nos, Mtr, Kg…" className="rounded-none mt-2 h-10" data-testid="adjust-unit" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Quantity Delta (+ add / − remove)</Label>
            <Input type="number" value={f.qty_delta} onChange={(e) => setF({ ...f, qty_delta: e.target.value })} className="rounded-none mt-2 h-10" data-testid="adjust-qty" />
          </div>
          <div>
            <Label className="label-eyebrow">Reason (required)</Label>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. opening stock / damage / audit correction" className="rounded-none mt-2 h-10" data-testid="adjust-reason" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={() => setOpen(false)} variant="outline" className="flex-1 rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10">Cancel</Button>
            <Button onClick={submit} className="flex-1 rounded-none bg-[#F59E0B] hover:bg-[#D97706] uppercase tracking-widest text-xs font-bold h-10" data-testid="adjust-submit">Record Adjustment</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
